import { type Breakpoint, createTheme, type Palette } from '@mui/material/styles';
import type { CSSProperties } from 'react';

export type WithBreakpoints<T> =
  | T
  | {
      [key in Breakpoint]?: T;
    };

declare module '@mui/material/styles' {
  interface TypographyVariants {
    xlHeadline: CSSProperties;
    xlGothamHeadline: CSSProperties;
    largeHeadline: CSSProperties;
    smallHeadline: CSSProperties;
    smallerHeadline: CSSProperties;
    headline: CSSProperties;
    body: CSSProperties;
    bodyLarge: CSSProperties;
    bodyLargeBold: CSSProperties;
    bodySmall: CSSProperties;

    title: React.CSSProperties;
    titleSmall: React.CSSProperties;
    textLargeBold: React.CSSProperties;
    textLarge: React.CSSProperties;
    textBold: React.CSSProperties;
    text: React.CSSProperties;
    textSmall: React.CSSProperties;
    textSmallBold?: React.CSSProperties;
    textExtraSmall: React.CSSProperties;
    accent: React.CSSProperties;
    accentSmall: React.CSSProperties;
    notesSmall: React.CSSProperties;
    notesHighlightSmall: React.CSSProperties;
    breadcrumb: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    xlHeadline?: CSSProperties;
    xlGothamHeadline?: CSSProperties;
    largeHeadline?: CSSProperties;
    smallHeadline?: CSSProperties;
    smallerHeadline?: CSSProperties;
    headline?: CSSProperties;
    body?: CSSProperties;
    bodyLarge?: CSSProperties;
    bodyLargeBold?: CSSProperties;
    bodySmall?: CSSProperties;
    bodyResponsive?: CSSProperties;

    title?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    textLargeBold?: React.CSSProperties;
    textLarge?: React.CSSProperties;
    textBold?: React.CSSProperties;
    text?: React.CSSProperties;
    textSmall?: React.CSSProperties;
    textSmallBold?: React.CSSProperties;
    textExtraSmall?: React.CSSProperties;
    accent?: React.CSSProperties;
    accentSmall?: React.CSSProperties;
    notesSmall?: React.CSSProperties;
    notesHighlightSmall?: React.CSSProperties;
    breadcrumb?: React.CSSProperties;
  }

  interface Palette {
    blues: {
      main: string;
      dark: string;
      darkest: string;
      light: string;
      lighter: string;
      'lighter-2': string;
      lightest: string;
      extraLight: string;
    };
    greens: {
      main: string;
      extraLight: string;
      light: string;
      lighter: string;
      'lighter-2': string;
      dark: string;
      lightest: string;
      darkest: string;
    };
    yellows: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      'lighter-2': string;
      lightest: string;
    };
    reds: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      lightest: string;
      extraLight: string;
    };
    neutral: {
      main: string;
      dark: string;
      darker: string;
      light: string;
      lighter: string;
      lightest: string;
    };
    lime: {
      main: string;
      tintLP: string;
      tintLP2: string;
      lightest: string;
    };
    nwGreens: {
      light: string;
      tintLP: string;
    };
  }

  interface PaletteOptions {
    blues: {
      main: string;
      dark: string;
      darkest: string;
      light: string;
      lighter: string;
      'lighter-2': string;
      lightest: string;
      extraLight: string;
    };
    greens: {
      main: string;
      extraLight: string;
      light: string;
      lighter: string;
      dark: string;
      lightest: string;
      darkest: string;
    };
    yellows: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      lightest: string;
    };
    reds: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      lightest: string;
      extraLight: string;
    };
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xlHeadline: true;
    xlGothamHeadline: true;
    largeHeadline: true;
    smallHeadline: true;
    smallerHeadline: true;
    headline: true;
    body: true;
    bodyLarge: true;
    bodyLargeBold: true;
    bodySmall: true;
    bodyResponsive: true;
    body1: false;
    body2: false;
    button: false;
    caption: false;
    overline: true;
    subtitle1: false;
    subtitle2: false;

    title: true;
    titleSmall: true;
    textLargeBold: true;
    textLarge: true;
    textBold: true;
    text: true;
    textSmall: true;
    textSmallBold?: true;
    textExtraSmall: true;
    accent: true;
    accentSmall: true;
    notesSmall: true;
    notesHighlightSmall: true;
    breadcrumb: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    projectGreen: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    creditScoreWidget: true;
    blueBordered: true;
    blueBorderedLighter: true;
    factChecked: true;
    factCheckedIconOnly: true;
    splashPage: true;
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

const breakpointConfig = {
  xs: 0,
  sm: 768,
  md: 1080,
  lg: 1280,
  xl: 1440,
  xxl: 1576,
};

/** @TODO This should be updated to include the breakpoint config, keeping as-is for backwards compatibility for now */
export const { breakpoints } = createTheme();

export type NWPalette = Palette;

export const palette = {
  text: {
    primary: '#000',
    secondary: '#64666A',
  },
  primary: {
    dark: '#006642',
    main: '#008254',
    light: '#40AD87',
  },
  secondary: {
    light: '#EEF7FF',
    main: '#005FB9',
  },
  grey: {
    '100': '#f7f7f7',
    '200': '#D8D9DA',
    '300': '#B1B3B5',
    '400': '#8B8C8F',
    '500': '#64666A',
    '800': '#262626',
  },
  info: {
    light: '#FFF7EA',
    dark: '#EFA92E',
    main: '#FBD083',
  },
  common: {
    white: '#ffffff',
    black: '#000',
  },
  blues: {
    main: '#005FB9',
    dark: '#02427E',
    darkest: '#02143A',
    light: '#54A0E8',
    lighter: '#9DCDFA',
    'lighter-2': '#C8E4FE',
    lightest: '#EEF7FF',
    extraLight: '#DEEFFF',
  },
  greens: {
    main: '#008254',
    extraLight: '#EFFCF8',
    light: '#E3FAF2',
    lighter: '#B7ECDA',
    'lighter-2': '#81D6B8',
    dark: '#006642',
    lightest: '#E3FAF2',
    darkest: '#00482F',
  },
  reds: {
    main: '#DE2828',
    dark: '#B40000',
    light: '#FF5D5E',
    lighter: '#FF9090',
    lightest: '#FFE8E8',
    extraLight: '#FFF7F7',
  },
  yellows: {
    main: '#FFC764',
    dark: '#EFA92E',
    light: '#FBD083',
    lighter: '#FFE2AE',
    'lighter-2': '#FFF0D6',
    lightest: '#FFF7EA',
  },
  neutral: {
    lightest: '#F5F5F5',
    lighter: '#D8D9DA',
    light: '#B1B3B5',
    main: '#8B8C8F',
    dark: '#64666A',
    darker: '#3D4045',
  },
  lime: {
    main: '#E2FF39',
    tintLP: '#DDFA88',
    tintLP2: '#E8FDAC',
    lightest: '#F8FFE4',
  },
  nwGreens: {
    light: '#40AD87',
    tintLP: '#A8EA4C',
  },
} as NWPalette;

/**
 * Nerdwallet uses a 4-point spacing system compared to MUI's 8-point system.
 * This reserves the first spacing numbers to match the NW styleguide, afterwards
 * a more standard range is applied to the supplied size.
 */

export const spacing = (size: number) => {
  switch (size) {
    case 0:
      return 0;
    case 1:
      return '0.25rem';
    case 2:
      return '0.5rem';
    case 3:
      return '1rem';
    case 3.5:
      return '1.5rem';
    case 4:
      return '1.75rem';
    case 5:
      return '2.75rem';
    default:
      return `${0.5 * size}rem`;
  }
};

export const iconSize = (size: string) => {
  switch (size) {
    case 'small':
      return '1rem';
    case 'medium':
      return '1.5rem';
    case 'large':
      return '2rem';
    case 'x-large':
      return '2.75rem';
    case 'xx-large':
      return '3.5rem';
    default:
      return size;
  }
};

export const GOTHAM = `var(--font-gotham, Gotham), sans-serif`;
export const GOTHAM_DISPLAY = `var(--font-gotham, GothamDisplay), sans-serif`;
export const CHRONICLE_DISPLAY = `var(--font-chronicle-display, Chronicle Display), serif`;

export const theme = createTheme({
  palette,
  spacing,
  breakpoints: {
    values: breakpointConfig,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html {
          -webkit-font-smoothing: auto;
        }
				:root {
					--color-black: #000;
					--color-green: ${palette.primary.main};
					--color-green130: ${palette.primary.dark};
					--color-body: var(--color-black);
					--button-bg: var(--color-green);
					--form-control-text: var(--color-body);
					--form-control-trim: #b4b9c1;
				}
				body, input, select, button {
					font-size: 16px;
				}
			`,
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${palette.secondary.main}`,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        fullWidth: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${palette.secondary.main}`,
          },
        },
        contained: {
          fontSize: 13,
          lineHeight: 1,
          textAlign: 'center',
          padding: 16,
          textTransform: 'uppercase',
          fontWeight: 'bold',
          borderRadius: 2,
          transitionDuration: '.15s',
          '&:hover': {
            background: palette.primary.dark,
          },
          '&:active': {
            background: palette.primary.main,
          },
        },
        outlined: {
          fontSize: 13,
          lineHeight: 1,
          textAlign: 'center',
          padding: 16,
          textTransform: 'uppercase',
          fontWeight: 'bold',
          borderRadius: 2,
          transitionDuration: '.15s',
        },
      },
      variants: [
        {
          props: { variant: 'projectGreen' },
          style: {
            letterSpacing: '0.0508rem',
            lineHeight: '13px !important',
            color: palette.lime.main,
            background: palette.greens.main,
            ':hover': {
              background: palette.greens.dark,
            },
          },
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: palette.secondary.main,
          fontFamily: GOTHAM,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiChip: {
      styleOverrides: {
        filled: {
          background: palette.grey[100],
        },
      },
      variants: [
        {
          props: { variant: 'blueBordered' },
          style: {
            border: '0.5px solid',
            borderColor: palette.blues.dark,
            color: palette.blues.dark,
            backgroundColor: palette.blues.lightest,
          },
        },
        {
          props: { variant: 'factCheckedIconOnly' },
          style: {
            border: '1px solid transparent',
            width: 'fit-content',
            color: palette.secondary.main,
            fontWeight: 500,
            fontFamily: GOTHAM_DISPLAY,
            backgroundColor: 'unset',
            height: 'fit-content',
            '& .MuiChip-label': {
              padding: 0,
            },
            '& .MuiSvgIcon-root': {
              color: 'inherit',
              width: '14px',
              height: '14px',
            },
          },
        },
        {
          props: { variant: 'factChecked' },
          style: {
            border: '1px solid',
            width: 'fit-content',
            borderColor: palette.secondary.main,
            color: palette.secondary.main,
            fontWeight: 500,
            fontFamily: GOTHAM_DISPLAY,
            backgroundColor: palette.blues.lightest,
            height: 'fit-content',
            '& .MuiChip-label': {
              padding: '0 0.5rem',
            },
            '& .MuiSvgIcon-root': {
              color: 'inherit',
              width: '14px',
              height: '14px',
            },
          },
        },
        {
          props: { variant: 'blueBorderedLighter' },
          style: {
            border: '1px solid',
            borderColor: palette.blues.lighter,
            color: palette.text.primary,
            fontWeight: 500,
            fontFamily: GOTHAM_DISPLAY,
            backgroundColor: palette.blues.lightest,
          },
        },
        {
          props: { variant: 'creditScoreWidget' },
          style: {
            height: 'auto',
            padding: `${spacing(1)} ${spacing(1)}`,
            marginBottom: spacing(2),
            svg: {
              marginLeft: spacing(1),
            },
            '& .MuiChip-label': {
              textTransform: 'capitalize',
              paddingLeft: spacing(1),
              whiteSpace: 'normal',
            },
          },
        },
        {
          props: { variant: 'splashPage' },
          style: {
            height: 'auto',
            padding: `${spacing(1)} ${spacing(4)}`,
            backgroundColor: palette.greens.main,
            '& .MuiChip-label': {
              textTransform: 'uppercase',
              letterSpacing: '0.0625em',
              color: 'white',
              fontWeight: 500,
              whiteSpace: 'normal',
            },
          },
        },
      ],
    },
    MuiCircularProgress: {
      defaultProps: {
        thickness: 2.5,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 16,
        },
        sizeSmall: {
          fontSize: 13,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: 'inherit', // once we remove theme-ui, this can probably be deleted
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${palette.grey[200]}`,
          '&:last-of-type': {
            borderBottom: `1px solid ${palette.grey[200]}`,
          },
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordion-root.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: spacing(3),
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': {
            outlineColor: palette.blues.light,
            outlineStyle: 'solid',
            outlineWidth: '2px',
            backgroundColor: palette.blues.lightest,
            zIndex: 1,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            fontSize: '20px',
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(-180deg)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-scroller': {
            [breakpoints.down('md')]: {
              overflowX: 'scroll !important',
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          fontSize: '24px',
          '&:before': {
            color: palette.common.white,
            border: `1px solid ${palette.grey['300']}`,
          },
        },
        tooltip: {
          padding: `${spacing(3)} ${spacing(3)} ${spacing(0)} ${spacing(3)}`,
          maxWidth: '36rem',
          backgroundColor: palette.common.white,
          boxShadow: '0 4px 8px 0 rgb(100 102 106 / 15%), 0 8px 16px 0 rgb(100 102 106 / 33%)',
          color: palette.text.primary,
          border: `1px solid ${palette.grey['300']}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          borderBottomColor: palette.common.black,
          fontSize: '1rem',
          paddingBottom: '8px',
        },
        root: {
          '&:first-of-type': {
            paddingLeft: 0,
          },
        },
      },
    },
  },
  typography: {
    fontFamily: GOTHAM,
    xlHeadline: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 36,
      fontWeight: 400,
      lineHeight: 1.1,
      [breakpoints.up('md')]: {
        fontSize: '3.375rem',
      },
    },
    xlGothamHeadline: {
      fontSize: 40,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.005em',
      [breakpoints.down(768)]: {
        fontSize: 24,
        letterSpacing: '0.01em',
      },
    },
    largeHeadline: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.167,
      [breakpoints.up('md')]: {
        fontSize: '2.25rem',
      },
    },
    smallHeadline: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    smallerHeadline: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '0.75rem',
    },
    headline: {
      fontWeight: 700,
      lineHeight: 1.33,
      fontSize: '1.5rem',
    },
    h1: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 32,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 36,
      },
    },
    h5: {
      fontFamily: CHRONICLE_DISPLAY,
    },
    h6: {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    body: {
      fontSize: 16,
      lineHeight: 1.5,
    },
    bodyLarge: {
      fontSize: 20,
      lineHeight: 1.5,
    },
    bodyLargeBold: {
      fontSize: 20,
      lineHeight: 1.3,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    bodySmall: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    bodyResponsive: {
      fontSize: 13,
      lineHeight: 1.5,
      [breakpoints.up(breakpointConfig.md)]: {
        fontSize: 16,
      },
    },

    /* Currency Type styles */
    title: {
      fontSize: 32,
      lineHeight: '111%',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 48,
        lineHeight: '53px',
      },
    },
    titleSmall: {
      fontSize: 24,
      lineHeight: '117%',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 36,
        lineHeight: '42px',
      },
    },
    textLargeBold: {
      fontSize: 20,
      lineHeight: '133%',
      fontWeight: 500,
      fontFamily: GOTHAM_DISPLAY,
      [breakpoints.up('md')]: {
        fontSize: 24,
        lineHeight: '32px',
      },
    },
    textLarge: {
      fontSize: 18,
      lineHeight: '133%',
      fontWeight: 300,
      [breakpoints.up('md')]: {
        fontSize: 20,
        lineHeight: '32px',
      },
    },
    textBold: {
      fontSize: 16,
      lineHeight: '150%',
      fontFamily: GOTHAM_DISPLAY,
      fontWeight: 500,
    },
    text: {
      fontSize: 16,
      lineHeight: '150%',
      fontWeight: 300,
    },
    textSmall: {
      fontSize: '.813rem',
      lineHeight: 1.4,
    },
    textSmallBold: {
      fontSize: '.813rem',
      lineHeight: 1.4,
      fontFamily: GOTHAM_DISPLAY,
      fontWeight: 500,
    },
    textExtraSmall: {
      fontSize: '0.688rem',
      fontWeight: 400,
      letterSpacing: '0.04rem',
      WebkitFontSmoothing: 'auto',
    },
    accent: {
      fontSize: 14,
      lineHeight: '15px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8125px',
    },
    accentSmall: {
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8125px',
    },
    notesSmall: {
      fontSize: 12,
      lineHeight: 1.5,
      fontWeight: 325,
      letterSpacing: '0.005em',
    },
    notesHighlightSmall: {
      fontSize: 10,
      lineHeight: 1.4,
      fontWeight: 500,
      color: palette.lime.tintLP2,
      letterSpacing: '0.005em',
      textTransform: 'uppercase',
    },
    breadcrumb: {
      fontSize: 10,
      lineHeight: '24px',
      letterSpacing: '.3px',
      textTransform: 'uppercase',
      fontWeight: 700,
    },
  },
});
